import {useEffect, useRef, useState} from 'react'
import CustomBox from '../common/CustomBox'
import SearchIcon from '@mui/icons-material/Search'
import {Box, InputAdornment, TextField} from '@mui/material'
import PropTypes from 'prop-types'
import {faqData} from './faqJson'
import {useTheme} from '@mui/material/styles'

function sentenceParts(fullPhrase, searchTermIndex) {
  let start = 0
  let end = fullPhrase.length
  if (fullPhrase.length > 100 && searchTermIndex !== -1) {
    start = Math.max(searchTermIndex - 50, 0)
    end = start + 100

    if (end > fullPhrase.length) {
      end = fullPhrase.length
      start = Math.max(end - 100, 0)
    }
  }
  return {start, end}
}

function processSuggestion(
  suggestion,
  escapedSearchTerm,
  searchTerminTitle,
  searchTermInDescription,
  searchTermInQuestion
) {
  let descriptionParts = []
  let questionParts = []
  let titleparts = []

  if (searchTermInDescription) {
    descriptionParts = suggestion.value.description
      .map((item) => {
        if (suggestion.value.description.length === 1) {
          return item
        } else if (suggestion.value.description.length > 1) {
          let combined = item.subdescription
          if (item.points) {
            combined += ' ' + item.points.join(' ')
          }
          return combined
        }
        return ''
      })
      .join(' ')
      .split(new RegExp(`(${escapedSearchTerm})`, 'gi'))
  }

  if (searchTermInQuestion) {
    questionParts = suggestion.value.question.split(new RegExp(`(${escapedSearchTerm})`, 'gi'))
  }
  if (searchTerminTitle) {
    titleparts = suggestion.value.title.split(new RegExp(`(${escapedSearchTerm})`, 'gi'))
  }

  return {titleparts, descriptionParts, questionParts}
}

function highlightSearchTerm(titleParts, questionParts, descriptionParts, escapedSearchTerm) {
  const fullQuestion = questionParts.join('')
  const searchTermQuestionIndex = fullQuestion.toLowerCase().indexOf(escapedSearchTerm.toLowerCase())
  const {questionStart, questionEnd} = sentenceParts(fullQuestion, searchTermQuestionIndex)
  const displayQuestion = `${questionStart > 0 ? '...' : ''}${fullQuestion.substring(questionStart, questionEnd)}${
    questionEnd < fullQuestion.length ? '...' : ''
  }`
  const partsToRenderQuestion = displayQuestion.split(new RegExp(`(${escapedSearchTerm})`, 'i'))

  const fullDescription = descriptionParts.join('')
  const searchTermDescriptionIndex = fullDescription.toLowerCase().indexOf(escapedSearchTerm.toLowerCase())
  const {start, end} = sentenceParts(fullDescription, searchTermDescriptionIndex)

  const displayDescription = `${start > 0 ? '...' : ''}${fullDescription.substring(start, end)}${
    end < fullDescription.length ? '...' : ''
  }`

  const partsToRenderDescription = displayDescription.split(new RegExp(`(${escapedSearchTerm})`, 'i'))

  const fullTitle = titleParts.join('')
  const searchTermTitleIndex = fullTitle.toLowerCase().indexOf(escapedSearchTerm.toLowerCase())

  const {titleStart, titleEnd} = sentenceParts(fullTitle, searchTermTitleIndex)

  const displayTitle = `${titleStart > 0 ? '...' : ''}${fullTitle.substring(titleStart, titleEnd)}${
    titleEnd < fullTitle.length ? '...' : ''
  }`

  const partsToRenderTitle = displayTitle.split(new RegExp(`(${escapedSearchTerm})`, 'i'))

  return {partsToRenderTitle, partsToRenderQuestion, partsToRenderDescription}
}

const SearchFAQ = ({searchTerm, setSearchTerm, handleItemClick, setIsAccordionTitleChange, setIsAccordionActive}) => {
  const theme = useTheme()
  const [suggestions, setSuggestions] = useState([])
  const suggestionsRef = useRef(null)
  const searchBoxRef = useRef(null)

  const matchesSearchTerm = (faq, searchTerm, title) => {
    return (
      title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      faq.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (faq.description.length === 1 && faq.description[0].toLowerCase().includes(searchTerm.toLowerCase())) ||
      (faq.description.length > 1 &&
        faq.description.some(
          (sub) =>
            sub.subdescription?.[0]?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            sub.points?.some((point) => point.toLowerCase().includes(searchTerm.toLowerCase()))
        ))
    )
  }

  useEffect(() => {
    if (searchTerm) {
      const results = Object.keys(faqData).flatMap((title) => {
        const titleMatch = title.toLowerCase().includes(searchTerm.toLowerCase())
        const contentMatches = faqData[title].Content.filter((faq) => matchesSearchTerm(faq, searchTerm, title))
        return [
          ...(titleMatch ? [{type: 'title', value: title}] : []),
          ...contentMatches.map((faq) => ({type: 'faq', value: faq}))
        ]
      })
      setSuggestions(results)
    } else {
      setSuggestions([])
    }
  }, [searchTerm])

  const handleClickOutside = (event) => {
    if (
      searchBoxRef.current &&
      !searchBoxRef.current.contains(event.target) &&
      suggestionsRef.current &&
      !suggestionsRef.current.contains(event.target)
    ) {
      setShowSuggestions(false)
    }
  }
  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const escapeRegExp = (string) => {
    return string ? string.replaceAll(/[.*+?^${}()|[\]\\]/g, String.raw`\$&`) : 'a^'
  }
  const [showSuggestions, setShowSuggestions] = useState(false)
  const handleTextFieldClick = () => {
    setShowSuggestions(true)
  }

  return (
    <CustomBox
      display='flex'
      marginBottom='20px'
      flexDirection='column'
      alignItems='center'
      position='relative'
      data-testid='search-faq'
      width='75%'
    >
      <TextField
        size='small'
        variant='outlined'
        margin='normal'
        fullWidth
        value={searchTerm}
        ref={searchBoxRef}
        onChange={(e) => {
          setSearchTerm(e.target.value)
          setShowSuggestions(true)
        }}
        onClick={handleTextFieldClick}
        placeholder='Search FAQs'
        InputProps={{
          sx: {
            height: '32px',
            borderRadius: '4px',
            fontSize: (theme) => theme.typography.subtitle1.fontSize
          },
          startAdornment: (
            <InputAdornment position='start'>
              <SearchIcon sx={{height: '20px'}} />
            </InputAdornment>
          )
        }}
      />
      {showSuggestions && (
        <Box
          ref={suggestionsRef}
          sx={{
            position: 'absolute',
            zIndex: '1',
            backgroundColor: theme.palette.hpWhite,
            boxShadow: '0px 1px 4px 0px #00000040',
            borderRadius: '5px',
            maxHeight: '200px',
            overflowY: 'auto',
            marginTop: '50px',
            width: '100%',
            alignSelf: 'start'
          }}
        >
          {searchTerm?.length >= 3 && suggestions?.length === 0 && (
            <Box
              sx={{
                padding: '15px',
                color: theme.palette.hpAccentBlack
              }}
            >
              Sorry we didn’t find any matches for &quot;<b>{searchTerm}</b>&quot; <br /> Send us an email at{' '}
              <a
                style={{color: theme.palette.hpPrimaryDark}}
                href='https://deloittesupportportal.service-now.com/csm'
                target='_blank'
                rel='noopener noreferrer'
              >
                Customer Support{' '}
              </a>{' '}
              .
            </Box>
          )}

          {/* search for keys in contents */}
          {searchTerm?.length >= 3 &&
            suggestions
              ?.filter((suggestion) => suggestion.type === 'title')
              .flatMap((suggestion) => {
                const escapedSearchTerm = escapeRegExp(searchTerm)
                const parts = suggestion.value.split(new RegExp(`(${searchTerm})`, 'gi'))
                const contentParts = []
                contentParts.push(
                  <Box
                    onClick={() => {
                      setSearchTerm('')
                      setSuggestions([])
                      setIsAccordionTitleChange(suggestion.value)
                      handleItemClick(suggestion.value)
                    }}
                    key={`contentTitle-${suggestion.value}`}
                    sx={{
                      padding: '15px',
                      cursor: 'pointer',
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                      color: theme.palette.hpAccentBlack
                    }}
                  >
                    {parts.map((part) => (
                      <span
                        key={`contentTitle-${suggestion.value}`}
                        style={
                          part.toLowerCase() === escapedSearchTerm.toLowerCase()
                            ? {fontWeight: theme.typography.fontWeightBold}
                            : {}
                        }
                      >
                        {part}
                      </span>
                    ))}
                  </Box>
                )
                return contentParts
              })}

          {/* search for terms inside accordions */}
          {searchTerm?.length >= 3 &&
            suggestions
              ?.filter((suggestion) => suggestion.type === 'faq')
              .flatMap((suggestion) => {
                const escapedSearchTerm = escapeRegExp(searchTerm)
                const searchTerminTitle = suggestion.value.title.toLowerCase().includes(escapedSearchTerm.toLowerCase())
                const searchTermInDescription =
                  suggestion.value.description.length === 1
                    ? suggestion.value.description[0].toLowerCase().includes(escapedSearchTerm.toLowerCase())
                    : suggestion.value.description.some(
                        (sub) =>
                          sub.subdescription[0]?.toLowerCase().includes(escapedSearchTerm.toLowerCase()) ||
                          sub.points?.some((point) => point.toLowerCase().includes(escapedSearchTerm.toLowerCase()))
                      )

                const searchTermInQuestion = suggestion.value.question
                  .toLowerCase()
                  .includes(escapedSearchTerm.toLowerCase())

                const {titleparts, descriptionParts, questionParts} = processSuggestion(
                  suggestion,
                  escapedSearchTerm,
                  searchTerminTitle,
                  searchTermInDescription,
                  searchTermInQuestion
                )

                const {partsToRenderTitle, partsToRenderQuestion, partsToRenderDescription} = highlightSearchTerm(
                  titleparts,
                  questionParts,
                  descriptionParts,
                  escapedSearchTerm
                )

                const suggestionsParts = []

                if (titleparts.length > 0) {
                  suggestionsParts.push(
                    <Box
                      onClick={() => {
                        handleItemClick(suggestion.value.id)
                        setIsAccordionActive(suggestion.value.id)
                        setSearchTerm('')
                        setSuggestions([])
                      }}
                      key={`t-${suggestion.value.id}`}
                      sx={{
                        padding: '15px',
                        cursor: 'pointer',
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                        textOverflow: 'ellipsis',
                        color: theme.palette.hpAccentBlack
                      }}
                    >
                      {partsToRenderTitle.map((part) => (
                        <span
                          key={`title-${suggestion.value.id}`}
                          style={
                            part.toLowerCase() === escapedSearchTerm.toLowerCase()
                              ? {fontWeight: theme.typography.fontWeightBold}
                              : {}
                          }
                          dangerouslySetInnerHTML={{__html: part}}
                        />
                      ))}
                    </Box>
                  )
                }

                if (questionParts.length > 0) {
                  suggestionsParts.push(
                    <Box
                      onClick={() => {
                        handleItemClick(suggestion.value.id)
                        setIsAccordionActive(suggestion.value.id)
                        setSearchTerm('')
                        setSuggestions([])
                      }}
                      key={`q-${suggestion.value.id}`}
                      sx={{
                        padding: '15px',
                        cursor: 'pointer',
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                        textOverflow: 'ellipsis',
                        color: theme.palette.hpAccentBlack
                      }}
                    >
                      {partsToRenderQuestion.map((part) => (
                        <span
                          key={`part-${suggestion.value.id}`}
                          style={
                            part.toLowerCase() === escapedSearchTerm.toLowerCase()
                              ? {fontWeight: theme.typography.fontWeightBold}
                              : {}
                          }
                          dangerouslySetInnerHTML={{__html: part}}
                        />
                      ))}
                    </Box>
                  )
                }

                if (descriptionParts.length > 0) {
                  suggestionsParts.push(
                    <Box
                      onClick={() => {
                        handleItemClick(suggestion.value.id)
                        setIsAccordionActive(suggestion.value.id)
                        setSearchTerm('')
                        setSuggestions([])
                      }}
                      key={`d-${suggestion.value.id}`}
                      sx={{
                        padding: '15px',
                        cursor: 'pointer',
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                        textOverflow: 'ellipsis',
                        color: theme.palette.hpAccentBlack
                      }}
                    >
                      {partsToRenderDescription
                        .filter((part) => part.trim() !== '')
                        .map((part) => (
                          <span
                            key={`part-${suggestion.value.id}`}
                            style={
                              part.toLowerCase() === escapedSearchTerm.toLowerCase()
                                ? {fontWeight: theme.typography.fontWeightBold}
                                : {}
                            }
                            dangerouslySetInnerHTML={{__html: part}}
                          />
                        ))}
                    </Box>
                  )
                }

                return suggestionsParts
              })}
        </Box>
      )}
    </CustomBox>
  )
}

SearchFAQ.propTypes = {
  searchTerm: PropTypes.string,
  setSearchTerm: PropTypes.func,
  handleItemClick: PropTypes.func,
  setIsAccordionTitleChange: PropTypes.func,
  setIsAccordionActive: PropTypes.func
}

export default SearchFAQ
